/**
 * Toast Store — lightweight notification system.
 *
 * Zustand-based store for managing toast notifications.
 * Toasts auto-dismiss after a configurable duration.
 */

import { create } from 'zustand';

export type ToastVariant = 'default' | 'success' | 'warning' | 'error';

export interface Toast {
  id: string;
  message: string;
  variant: ToastVariant;
  duration: number;
}

interface ToastState {
  toasts: Toast[];
  addToast: (message: string, variant?: ToastVariant, duration?: number) => void;
  removeToast: (id: string) => void;
}

const DEFAULT_DURATION = 3500;

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],

  addToast: (message, variant = 'default', duration = DEFAULT_DURATION) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const toast: Toast = { id, message, variant, duration };

    set((state) => ({ toasts: [...state.toasts, toast] }));

    setTimeout(() => {
      set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
    }, duration);
  },

  removeToast: (id) => {
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
  },
}));

/** Convenience function — call from anywhere without hooks. */
export function toast(message: string, variant?: ToastVariant, duration?: number): void {
  useToastStore.getState().addToast(message, variant, duration);
}
